import { createFileRoute, Link } from "@tanstack/react-router";
import { getComparePairs } from "@/lib/compare.functions";
import type { ComparePairEntry } from "@/lib/compare.server";
import { breadcrumbLd, itemListLd, SITE_OG_IMAGE } from "@/lib/seo";
import { faqLd } from "@/lib/seo";
import { FaqBlock } from "@/components/FaqBlock";

const COMPARE_FAQS = [
  {
    q: "How are the two vehicles compared?",
    a: "Each matchup puts the two vehicles side by side on EPA combined, city, and highway MPG, estimated annual fuel cost at 15,000 miles a year, engine displacement and cylinder count, transmission type, drivetrain, and EPA vehicle class. Every figure comes from the same official dataset for both vehicles, so the comparison is apples to apples rather than one manufacturer's number against another's.",
  },
  {
    q: "Why can't I compare any two vehicles?",
    a: "Comparison pages are published only for pairs that share an EPA vehicle class and both have enough recorded trim data to make the table meaningful. A midsize sedan against a heavy-duty pickup would produce a table nobody can act on, so those pairs are left out deliberately.",
  },
  {
    q: "Which number matters most when comparing two cars?",
    a: "Combined MPG for the specific model year and drivetrain you are considering, followed by estimated annual fuel cost. Over five years and 75,000 miles, a 4 MPG difference between two otherwise similar vehicles typically works out to several thousand dollars — usually larger than the sticker gap between trims.",
  },
  {
    q: "Are hybrids and EVs included?",
    a: "Yes. Hybrids are shown with their EPA MPG ratings, and electric vehicles use MPGe along with kWh per 100 miles so you can convert to your own electricity rate. For a full gas-versus-electric ownership picture, use the Gas vs EV cost calculator rather than the MPG figure alone.",
  },
];

const DESCRIPTION =
  "Car compare side by side: put any two vehicles side by side on EPA combined MPG, city and highway fuel economy, annual fuel cost, engines, transmissions, and drivetrain — all from official test data.";

export const Route = createFileRoute("/compare/")({
  loader: async () => await getComparePairs(),
  head: ({ loaderData }) => ({
    meta: [
      { title: "Compare Cars & SUVs Side by Side: Fuel Economy | AutoSpecData" },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: "Compare Cars Side by Side" },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: "https://autospecdata.com/compare" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:image", content: SITE_OG_IMAGE },
      { name: "twitter:image", content: SITE_OG_IMAGE },
    ],
    links: [{ rel: "canonical", href: "https://autospecdata.com/compare" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(
          itemListLd(
            "Popular vehicle comparisons",
            ((loaderData?.pairs ?? []) as ComparePairEntry[]).map((p) => ({
              name: `${p.a} vs ${p.b}`,
              path: `/compare/${p.slug}`,
            })),
          ),
        ),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbLd([
            { name: "Home", path: "/" },
            { name: "Compare", path: "/compare" },
          ]),
        ),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(faqLd(COMPARE_FAQS)),
      },
    ],
  }),
  component: CompareIndex,
});

function CompareIndex() {
  const { pairs } = Route.useLoaderData();
  const all = pairs as ComparePairEntry[];
  const featured = all.slice(0, 24);
  const rest = all.slice(24);
  return (
    <div>
      <nav className="mx-auto max-w-5xl px-4 sm:px-6 pt-4 text-sm text-muted-foreground">
        <Link to="/" className="hover:text-foreground">
          Home
        </Link>
        <span className="mx-2">/</span>
        <span className="text-foreground">Compare</span>
      </nav>
      <section className="border-b border-border bg-gradient-to-b from-secondary/40 to-background">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 py-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-foreground">
            Compare Cars Side by Side: Fuel Economy, MPG &amp; Specs
          </h1>
          <p data-quick-answer className="mt-4 max-w-3xl text-base leading-relaxed text-foreground">
            {DESCRIPTION}
          </p>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-muted-foreground">
            Compare SUVs side by side, or any two sedans, trucks, hybrids or EVs, and read the
            side by side fuel economy figures — combined, city and highway miles per gallon —
            straight from the EPA test record rather than a manufacturer brochure. Each matchup
            also shows estimated annual fuel cost at 15,000 miles a year, so you can compare car
            fuel consumption in dollars as well as miles per gallon.
          </p>
          <Link
            to="/search"
            search={{ q: "", make: "", model: "", year: undefined, type: "", page: 1 }}
            className="cta-gold mt-6"
          >
            Find a vehicle to compare
          </Link>
        </div>
      </section>
      <section className="mx-auto max-w-5xl px-4 sm:px-6 py-8">
        <h2 className="text-xl font-bold text-foreground mb-4">Popular matchups</h2>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {featured.map((p) => (
            <Link
              key={p.slug}
              to="/compare/$pair"
              params={{ pair: p.slug }}
              className="rounded-lg border border-border bg-card p-4 transition-colors hover:border-primary"
            >
              <span className="block font-semibold text-foreground">
                {p.a} <span className="text-muted-foreground">vs</span> {p.b}
              </span>
              <span className="mt-1 block text-xs text-muted-foreground">{p.cls}</span>
            </Link>
          ))}
        </div>
        {rest.length > 0 && (
          <div className="mt-10">
            <h2 className="text-xl font-bold text-foreground">
              All {all.length} published matchups
            </h2>
            <ul className="mt-4 grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2 lg:grid-cols-3">
              {rest.map((p) => (
                <li key={p.slug}>
                  <Link
                    to="/compare/$pair"
                    params={{ pair: p.slug }}
                    className="text-muted-foreground hover:text-primary hover:underline"
                  >
                    {p.a} vs {p.b}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}
        <p className="mt-6 text-sm text-muted-foreground">
          Looking for a matchup that isn't listed? Every model pair in the same EPA class
          has a page — find either vehicle through{" "}
          <Link
            to="/search"
            search={{ q: "", make: "", model: "", year: undefined, type: "", page: 1 }}
            className="text-primary underline"
          >
            search
          </Link>{" "}
          and follow its comparison links.
        </p>

        <h2 className="mt-12 text-xl font-bold text-foreground">
          How AutoSpecData comparisons work
        </h2>
        <div className="mt-3 space-y-3 text-sm leading-relaxed text-muted-foreground">
          <p>
            Most car comparisons online mix manufacturer marketing claims with third-party
            estimates, which makes the two columns impossible to trust against each other. Every
            comparison here is built from one source set: US EPA and Department of Energy fuel
            economy test data, cross-checked against NHTSA vPIC vehicle records. Both sides of
            the table are measured the same way, in the same laboratory cycle, for the same model
            year — so a 3 MPG gap on the page is a real 3 MPG gap, not a difference in testing
            methodology.
          </p>
          <p>
            Each matchup page shows city, highway, and combined MPG, estimated annual fuel cost,
            engine displacement and cylinder count, transmission, drivetrain, and EPA vehicle
            class for both vehicles, with the more efficient figure highlighted in each row.
            Where a model offered several engines or drivetrains in the same year, the comparison
            uses the most representative configuration and the model pages break out the rest.
          </p>
          <p>
            Pairs are filtered before publication: two vehicles must share a vehicle class and
            both must have enough trim rows to fill the table. That keeps the library focused on
            matchups a real shopper would actually cross-shop, instead of every mathematically
            possible combination.
          </p>
        </div>

        <h2 className="mt-10 text-xl font-bold text-foreground">
          Comparing cars: common questions
        </h2>
        <div className="mt-4">
          <FaqBlock items={COMPARE_FAQS} />
        </div>
      </section>
    </div>
  );
}
